import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import logo from '../../assets/AIST_Logo_QR.svg';
import './StyledQRCode.css';

const BRAND_COLOR = '#5FA6D9';

/** Shared QR styling config — used by both StyledQRCode and bulk download */
export const getQRConfig = (value, size = 1024) => ({
  width: size,
  height: size,
  type: 'canvas',
  data: value || ' ',
  image: logo,
  margin: 10,
  qrOptions: { errorCorrectionLevel: 'H' },
  backgroundOptions: { color: '#FFFFFF' },
  dotsOptions: { color: BRAND_COLOR, type: 'extra-rounded' },
  cornersSquareOptions: { color: BRAND_COLOR, type: 'extra-rounded' },
  cornersDotOptions: { color: BRAND_COLOR, type: 'dot' },
  imageOptions: { crossOrigin: 'anonymous', margin: 6, imageSize: 0.28, hideBackgroundDots: true },
});

const StyledQRCode = forwardRef(({ value, size = 320 }, ref) => {
  const containerRef = useRef(null);
  const qrRef = useRef(null);

  // Create the QR instance once, on mount
  useEffect(() => {
    qrRef.current = new QRCodeStyling({
      width: size,
      height: size,
      type: 'canvas',
      data: value || ' ',
      image: logo,
      margin: 10,
      qrOptions: {
        errorCorrectionLevel: 'H', // High EC — required so the logo doesn't break scanning
      },
      backgroundOptions: {
        color: '#FFFFFF',
      },
      dotsOptions: {
        color: BRAND_COLOR,
        type: 'extra-rounded',
      },
      cornersSquareOptions: {
        color: BRAND_COLOR,
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: BRAND_COLOR,
        type: 'dot',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 6,
        imageSize: 0.28,
        hideBackgroundDots: true,
      },
    });

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      qrRef.current.append(containerRef.current);
      // Scale canvas to fit container while keeping full resolution for download
      const canvas = containerRef.current.querySelector('canvas');
      if (canvas) {
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update data / size when props change
  useEffect(() => {
    if (qrRef.current) {
      qrRef.current.update({
        data: value || ' ',
        width: size,
        height: size,
      });
    }
  }, [value, size]);

  // Expose download() to parent
  useImperativeHandle(ref, () => ({
    download: (filename) => {
      if (qrRef.current) {
        return qrRef.current.download({
          name: filename || 'qr-code',
          extension: 'png',
        });
      }
      return Promise.reject(new Error('QR instance not ready'));
    },
  }));

  return (
    <div
      ref={containerRef}
      className="styled-qr-canvas-wrapper"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    />
  );
});

StyledQRCode.displayName = 'StyledQRCode';

export default StyledQRCode;
